/** @jsx jsx */
import React, { forwardRef, useMemo, useRef } from 'react';
import { css, jsx, styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Avatar from '@atlaskit/avatar';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { HoverPopover } from '@atlassian/jira-hover-popover/src/ui/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { useBespokeEmptyFieldTextExperiment } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-experiment/index.tsx';
import mergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import { VIEW_PROFILE_ACTION } from '@atlassian/jira-profilecard-next/src/common/constants.tsx';
import type { Actions } from '@atlassian/jira-profilecard-next/src/common/types.tsx';
import AsyncProfileCardNext from '@atlassian/jira-profilecard-next/src/ui/profilecard/sync-experiment.tsx';
import type { singleUserPicker_issueFieldSingleUserPickerReadviewFull_SingleUserPickerReadView_fragmentRef$key as SingleUserPickerFragment } from '@atlassian/jira-relay/src/__generated__/singleUserPicker_issueFieldSingleUserPickerReadviewFull_SingleUserPickerReadView_fragmentRef.graphql';
import type { singleUserPicker_issueFieldSingleUserPickerReadviewFull_SingleUserPickerWithProfileCardReadView_fragmentRef$key as SingleUserPickerWithProfileCardFragment } from '@atlassian/jira-relay/src/__generated__/singleUserPicker_issueFieldSingleUserPickerReadviewFull_SingleUserPickerWithProfileCardReadView_fragmentRef.graphql';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import messages from './messages.tsx';
import type {
	SingleUserPickerReadViewProps,
	SingleUserPickerWithProfileCardReadViewProps,
} from './types.tsx';

/**
 * The SingleUserPickerReadView will show a read only view of SingleUserPicker field.
 * @param props [SingleUserPickerReadViewProps](./types.tsx)
 */
export const SingleUserPickerReadView = ({
	fragmentRef,
	emptyUserLabel = null,
	shouldRenderHoverPopover,
	fieldId,
}: SingleUserPickerReadViewProps) => {
	const data = useFragment<SingleUserPickerFragment>(
		graphql`
			fragment singleUserPicker_issueFieldSingleUserPickerReadviewFull_SingleUserPickerReadView_fragmentRef on JiraSingleSelectUserPickerField {
				user {
					name
					picture
					accountStatus
				}
			}
		`,
		fragmentRef,
	);
	const { formatMessage } = useIntl();
	const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();

	const {
		name = emptyUserLabel ?? formatMessage(messages.anonymousOption),
		picture,
		accountStatus,
	} = data?.user || {};

	const contentRef = useRef(null);

	if (!data?.user && fieldId !== 'assignee' && fieldId !== 'reporter' && isExperimentEnabled) {
		return <EmptyFieldText emptyText={formatMessage(messages.noValueTextExperiment)} />;
	}

	if (shouldRenderHoverPopover && data?.user === null) {
		return (
			<HoverPopover content={name} contentRef={contentRef}>
				{({ ref }) => (
					<Container>
						<Avatar
							as={AvatarAlignmentFix}
							src={picture}
							name={name}
							size="small"
							borderColor="transparent"
						/>
						<Text maxLines={1} ref={mergeRefs(ref, contentRef)}>
							{name}
						</Text>
					</Container>
				)}
			</HoverPopover>
		);
	}

	return (
		// We use our own container element rather than an AvatarItem as we've observed hydration bugs with SSR+Emotion
		// when this component is wrapped in an async profile card, and this approach is the path of least friction.
		// Further context here https://atlassian.slack.com/archives/C060GTVV988/p1712624943348969.
		<Container>
			<Avatar
				as={AvatarAlignmentFix}
				src={picture}
				name={name}
				size="small"
				borderColor="transparent"
			/>
			<TextContainer>
				<span>{name}</span>
				{data?.user && accountStatus !== 'active' && (
					<UserStatus>{`(${formatMessage(messages.deactivated)})`}</UserStatus>
				)}
			</TextContainer>
		</Container>
	);
};

export const SingleUserPickerWithProfileCardReadView = ({
	fragmentRef,
	emptyUserLabel,
	profileCardAction,
	shouldRenderHoverPopover,
	fieldId,
}: SingleUserPickerWithProfileCardReadViewProps) => {
	const data = useFragment<SingleUserPickerWithProfileCardFragment>(
		graphql`
			fragment singleUserPicker_issueFieldSingleUserPickerReadviewFull_SingleUserPickerWithProfileCardReadView_fragmentRef on JiraSingleSelectUserPickerField {
				...singleUserPicker_issueFieldSingleUserPickerReadviewFull_SingleUserPickerReadView_fragmentRef
				user {
					accountId
					name
				}
			}
		`,
		fragmentRef,
	);

	const accountId: string | null = data?.user?.accountId || null;

	const actions = useMemo(
		(): Actions[] =>
			profileCardAction ? [VIEW_PROFILE_ACTION, profileCardAction] : [VIEW_PROFILE_ACTION],
		[profileCardAction],
	);

	if (accountId) {
		return (
			<UFOSegment
				name="issue-field-single-user-picker-read-view"
				{...(fg('empanada_collapse_user_picker_segment_into_list') ? { mode: 'list' } : {})}
			>
				<AsyncProfileCardNext
					{...(fg('aria-label-for-issue-reporter-or-assignee-card')
						? { prepopulatedData: { fullName: data?.user?.name } }
						: {})}
					accountId={accountId}
					actions={actions}
				>
					<SingleUserPickerReadView
						fragmentRef={data}
						emptyUserLabel={emptyUserLabel}
						shouldRenderHoverPopover={shouldRenderHoverPopover}
						fieldId={fieldId}
					/>
				</AsyncProfileCardNext>
			</UFOSegment>
		);
	}

	return (
		<UFOSegment
			name="issue-field-single-user-picker-read-view"
			{...(fg('empanada_collapse_user_picker_segment_into_list') ? { mode: 'list' } : {})}
		>
			<SingleUserPickerReadView
				fragmentRef={data}
				emptyUserLabel={emptyUserLabel}
				shouldRenderHoverPopover={shouldRenderHoverPopover}
				fieldId={fieldId}
			/>
		</UFOSegment>
	);
};

/**
 * Component to pass for "as" prop of Avatar. It reverses the 2px margin added in the Avatar
 * component when a transparent border is used.
 *
 * Done using div + css because styled components are not compatible with Avatar's types.
 */
const AvatarAlignmentFix = forwardRef<HTMLDivElement, React.AllHTMLAttributes<HTMLElement>>(
	(props, ref) => <div {...props} ref={ref} css={avatarAlignmentStyles} />,
);

const avatarAlignmentStyles = css({
	marginTop: token('space.negative.025'),
	marginRight: token('space.negative.025'),
	marginBottom: token('space.negative.025'),
	marginLeft: token('space.negative.025'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	maxWidth: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextContainer = styled.span({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UserStatus = styled.span({
	color: token('color.text.subtlest'),
	marginLeft: token('space.025'),
});
